import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Button } from "@mui/material";
import MuiAccordion, {
  accordionClasses,
  AccordionProps as MuiAccordionProps,
} from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  accordionSummaryClasses,
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { BoxProps, Theme } from "@mui/system";
import { Fragment, useState } from "react";
import { Typography } from "@platform/design-system";

type onEdit<T> = (section: T) => void;

interface Data {
  title: string;
  values: {
    label: string;
    value: string | number | null;
    isMissing?: boolean;
  }[];
}

type ExpandedState = {
  all?: boolean;
  [key: string]: boolean | undefined;
};

interface Section {
  id: string;
  headers: string[];
  data: Data[];
  isMissingHeaders?: Record<number, boolean | undefined>;
}

interface AccordionProps extends Omit<MuiAccordionProps, "children"> {
  section: Section;
  hasSectionData?: boolean;
  onEdit?: onEdit<Section>;
}

interface CoveragesAccordionProps<T extends Section> {
  headers: string[];
  sections: T[];
  onEdit?: onEdit<T>;
  slotProps?: Partial<{
    container: Partial<BoxProps>;
  }>;
}

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "1rem" }} />}
    {...props}
  />
))(({ theme }: { theme: Theme }) => ({
  flexDirection: "row-reverse",
  [`&.${accordionSummaryClasses.expanded}`]: {
    borderBottom: `1px solid ${theme.palette["divider"]}`,
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
}));

const Accordion = styled(
  ({ section, hasSectionData, onEdit, ...props }: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} {...props}>
      <AccordionSummary>
        <Box component="span" display="flex" width="100%">
          {section.headers.map((item, index) => (
            <Typography
              key={`header-${item}`}
              flex={1}
              variant="body-medium"
              component="span"
              pl={2}
              color={section.isMissingHeaders?.[index] ? "danger" : "inherit"}
            >
              {item}
            </Typography>
          ))}
        </Box>
      </AccordionSummary>
      {hasSectionData ? (
        <MuiAccordionDetails sx={{ paddingInlineStart: 6 }}>
          <Box component="table" sx={{ borderCollapse: "collapse" }}>
            <Box component="tbody">
              {section.data.map((section) => (
                <Fragment key={`section-${section.title}`}>
                  <Box component="tr">
                    <Box component="td" colSpan={2}>
                      <Typography variant="body-medium" sx={{ mb: 1, mt: 2 }}>
                        <strong>{section.title}</strong>
                      </Typography>
                    </Box>
                  </Box>
                  {section.values.map((kv) => (
                    <Box key={`keyvalue-${kv.label}`} component="tr">
                      <Box component="td" sx={{ py: 1, pr: 10 }}>
                        <Typography
                          variant="body-medium"
                          color={kv.isMissing ? "danger" : "inherit"}
                        >
                          {kv.label}
                        </Typography>
                      </Box>
                      <Box
                        component="td"
                        sx={{ py: 1, wordBreak: "break-word" }}
                      >
                        <Typography
                          variant="body-medium"
                          color={kv.isMissing ? "danger" : "inherit"}
                        >
                          {kv.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Fragment>
              ))}
              {onEdit ? (
                <Box component="tr">
                  <Box component="td" colSpan={2}>
                    <Button
                      onClick={() => onEdit(section)}
                      variant="text"
                      sx={{
                        p: 0,
                        minWidth: "auto",
                        textTransform: "none",
                        textDecoration: "underline",
                      }}
                    >
                      edit
                    </Button>
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Box>
        </MuiAccordionDetails>
      ) : null}
    </MuiAccordion>
  ),
)(({ theme }) => ({
  "&:not(:last-child)": {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${accordionClasses.expanded}:first-of-type`]: {
    borderBottom: "none",
  },
  "&:before": {
    display: "none",
  },
}));

function CoveragesAccordion<T extends Section>({
  headers,
  sections,
  slotProps = {},
  onEdit,
}: CoveragesAccordionProps<T>): React.JSX.Element {
  const [expanded, setExpanded] = useState<ExpandedState>(() =>
    sections.reduce<ExpandedState>(
      (acc, section) => {
        acc[section.id] = false;
        return acc;
      },
      { all: false },
    ),
  );

  const handleToggleAll = (
    _: React.SyntheticEvent,
    isExpanded: boolean,
  ): void => {
    setExpanded((prev) =>
      Object.keys(prev).reduce<ExpandedState>(
        (acc, key) => {
          acc[key] = isExpanded;
          return acc;
        },
        { all: isExpanded },
      ),
    );
  };

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(({ all: _, ...prev }) => {
        const newExpanded = { ...prev, [panel]: isExpanded };
        const all = Object.values(newExpanded).every(Boolean);
        return { ...newExpanded, all };
      });
    };

  return (
    <Box {...slotProps.container}>
      <Accordion
        expanded={expanded.all}
        onChange={handleToggleAll}
        section={{ id: "all", headers, data: [] }}
        hasSectionData={false}
        sx={{ fontWeight: "fontWeightBold" }}
        slotProps={{
          heading: {
            sx: {
              [`& .${accordionSummaryClasses.content}`]: {
                fontWeight: "fontWeightMedium",
              },
            },
          },
        }}
      />
      {sections.map((section) => {
        const hasSectionData = section.data.length > 0;
        return (
          <Accordion
            key={section.id}
            expanded={expanded[section.id]}
            onChange={hasSectionData ? handleChange(section.id) : undefined}
            hasSectionData={hasSectionData}
            section={section}
            onEdit={onEdit}
          />
        );
      })}
    </Box>
  );
}

export type { Section as CoverageAccordionSection };
export { CoveragesAccordion };
